import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { offerta, ordine, azienda } from '@/model/qsadminapi/QsAdminApiModuleModel'
import OrderEditor from '@/routes/orders/OrderEditor'
import { RoleRouteObject, RouteFunctionParams } from '@/types'
import { redirect } from 'react-router-dom'
import { LoaderFunction, ActionFunction, defer } from 'react-router-typesafe'
import { ODataModelResponseV4 } from '@odata2ts/odata-core'
import { HttpResponseModel, ODataClientError } from '@odata2ts/http-client-api'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import { Roles } from '@/auth/azure/Roles'
import { StatoAcconto, StatoFatturazioneOrdine, StatoOrdine } from '@/routes/orders/order.types'
import { StatoOfferta } from '@/routes/offers/OfferEditor.enums'
import { StatoCommessa } from '@/routes/projects/ProjectEditor.enums'

export const orderEditorRouteLoader = (async ({ request, params }: RouteFunctionParams<'orderId' | 'clientId'>) => {
  const orderId = params.orderId
  const clientId = params.clientId
  const offerId = new URL(request.url).searchParams.get('offerId')
  const isConsuntivo = new URL(request.url).searchParams.get('isConsuntivo') === 'true'

  const qsAdminApi = useQsAdminApiManager.getState().service

  let azienda: HttpResponseModel<ODataModelResponseV4<azienda>> | undefined
  let title = azienda ? `${azienda.data.nome} - ` : ``
  let order: ordine

  const userInfo = await getAzureUserInformation()
  const user = await qsAdminApi.user().query((builder, user) => {
    builder.select('id', 'fullname', 'email', 'azienda')
    builder.expanding('azienda', (riferimentiBuilder, azienda) => {
      riferimentiBuilder.select('id', 'nome')
    })
    builder.filter(user.email.eq(userInfo?.email ?? ''))
  })

  if (clientId)
    azienda = await qsAdminApi.azienda(Number(clientId)).query((builder, azienda) => {
      builder.expanding('gruppo_aziendale', (gruppo_aziendaleBuilder, gruppo_aziendale) => {
        gruppo_aziendaleBuilder.select('id', 'nome')
      })
      builder.select('id', 'nome', 'gruppo_aziendale')
    })

  if (orderId) {
    //editor
    const getOrder = await qsAdminApi
      .ordine(Number(orderId))
      .query((builder, ordine) => {
        builder.expanding('stato', (statoBuilder, stato) => {
          statoBuilder.select('id', 'nome')
        })
        builder.expanding('offerta', (offertaBuilder, offerta) => {
          offertaBuilder.expanding('analisi', (analisiBuilder, analisi) => {
            analisiBuilder.select('id', 'ded_Dis')
          })
          offertaBuilder.select('id', 'ded_Dis', 'analisi')
        })
        builder.expand('quota')
        builder.expanding('commessa', (commessaBuilder, commessa) => {
          commessaBuilder.expanding('sede', (sedeCommessaBuilder, sedeCommessa) => {
            sedeCommessaBuilder.expanding('azienda', (aziendaSedeCommessaBuilder, aziendaSede) => {
              aziendaSedeCommessaBuilder.expanding('agente', (agenteBuilder, agente) => {
                agenteBuilder.expanding('commerciale_qs', (commercialeBuilder, commerciale) => {
                  commercialeBuilder.select('id', 'fullname')
                })
                agenteBuilder.select('id', 'commerciale_qs')
              })
              aziendaSedeCommessaBuilder.expanding('rivenditore', (rivenditoreBuilder, rivenditore) => {
                rivenditoreBuilder.select('id', 'nome')
              })
              aziendaSedeCommessaBuilder.expanding('gruppo_aziendale', (gruppo_aziendaleBuilder, gruppo_aziendale) => {
                gruppo_aziendaleBuilder.select('id', 'nome')
              })
              aziendaSedeCommessaBuilder.select('id', 'nome', 'agente', 'rivenditore', 'gruppo_aziendale')
            })
            sedeCommessaBuilder.select('id', 'nome', 'azienda', 'note')
          })
          commessaBuilder.select('id', 'ded_Dis', 'titolo', 'sede')
        })
        builder.expanding('acconto', (accontoBuilder, acconto) => {
          accontoBuilder.select('id', 'nome')
        })
        builder.expanding('fatturazione', (fatturazioneBuilder, fatturazione) => {
          fatturazioneBuilder.select('id', 'nome')
        })
        builder.expanding('assegnato', (assegnatoBuilder, assegnato) => {
          assegnatoBuilder.select('id', 'fullname', 'email')
        })
        builder.expanding('punti_fatturazione_ordine', (puntoFatturazioneBuilder, puntoFatturazione) => {
          puntoFatturazioneBuilder.expanding('stato', (statoBuilder, stato) => {
            statoBuilder.select('id', 'nome')
          })
          puntoFatturazioneBuilder.select('id', 'ded_Dis', 'stato')
        })
      })
      .catch((error: ODataClientError) => {
        if (error.status === 404) throw new Error('404 Not Found: Ordine non trovato')
        throw new Error()
      })
    order = getOrder.data satisfies ordine
    title += `${order.ded_Dis}`
    if (clientId && order?.commessa?.sede?.azienda && order?.commessa?.sede?.azienda.id !== Number(clientId)) {
      throw new Error(
        `L'ordine ${order.ded_Dis} appartiene al cliente ${order?.commessa?.sede?.azienda.nome} e non al cliente corrente`,
      )
    }
  } else {
    //creator
    title += `Nuovo ordine`
    let getOffer: HttpResponseModel<ODataModelResponseV4<offerta>> | undefined
    if (offerId) {
      //creator a partire da offerta
      getOffer = await qsAdminApi.offerta(Number(offerId)).query((builder, offer) => {
        builder.expanding('commessa', (commessaBuilder, commessa) => {
          commessaBuilder.expanding('sede', (sedeBuilder, sede) => {
            sedeBuilder.expanding('azienda', (aziendaBuilder, azienda) => {
              aziendaBuilder.expanding('agente', (agenteBuilder, agente) => {
                agenteBuilder.expanding('commerciale_qs', (commercialeBuilder, commerciale) => {
                  commercialeBuilder.select('id', 'fullname')
                })
                agenteBuilder.select('id', 'commerciale_qs')
              })
              aziendaBuilder.expanding('rivenditore', (rivenditoreBuilder, rivenditore) => {
                rivenditoreBuilder.select('id', 'nome')
              })
              aziendaBuilder.expanding('gruppo_aziendale', (gruppo_aziendaleBuilder, gruppo_aziendale) => {
                gruppo_aziendaleBuilder.select('id', 'nome')
              })
              aziendaBuilder.select('id', 'nome', 'agente', 'rivenditore', 'gruppo_aziendale')
            })
            sedeBuilder.select('id', 'nome', 'azienda', 'note')
          })
          commessaBuilder.select('id', 'ded_Dis', 'titolo', 'sede')
        })
        builder.expanding('analisi', (analisiBuilder, analisi) => {
          analisiBuilder.select('id', 'ded_Dis')
        })
      })
    }
    const getStatoDefault = await qsAdminApi.stato_ordine().query((builder, stato) => {
      builder.filter(stato.nome.eq(isConsuntivo || offerId ? StatoOrdine.DA_EVADERE : StatoOrdine.PROVVISORIO))
      builder.select('id', 'nome')
    })
    const getStatoAccontoDefault = await qsAdminApi.stato_acconto_ordine().query((builder, stato) => {
      builder.filter(stato.nome.eq(StatoAcconto.NON_DOVUTO))
      builder.select('id', 'nome')
    })
    const getStatoFatturazioneDefault = await qsAdminApi.stato_fatturazione_ordine().query((builder, stato) => {
      builder.filter(stato.nome.eq(StatoFatturazioneOrdine.DA_FATTURARE))
      builder.select('id', 'nome')
    })
    order = {
      id: 0,
      ded_Dis: '',
      ded_RootFam: '',
      ded_SubFam: '',
      ded_Id: 0,
      creazione: new Date().toISOString(),
      evasione: null,
      importo: 0,
      note: null,
      note_evasione: null,
      anno_rif: new Date().getFullYear(),
      stato: getStatoDefault.data.value[0],
      acconto: getStatoAccontoDefault.data.value[0],
      fatturazione: getStatoFatturazioneDefault.data.value[0],
      assegnato: null,
      offerta: getOffer?.data,
      quota: null,
      commessa: getOffer?.data.commessa,
      tasks: [],
      attachmentsFolderUrl: '',
      mainDocumentUrl: '',
      attivitaTecnicaRichiesta: getOffer ? getOffer.data.attivitaTecnicaRichiesta : false,
      hasTasks: false,
      consuntivo: isConsuntivo,
    }
  }

  //query di popolazione lookup
  const getOfferte =
    order.commessa?.sede || clientId
      ? qsAdminApi.offerta().query((builder, offerta) => {
          if (order.commessa?.sede?.azienda?.gruppo_aziendale?.id)
            builder.filter(
              offerta.sede.props.azienda.props.gruppo_aziendale.props.id.eq(
                order.commessa.sede.azienda.gruppo_aziendale.id,
              ),
            )
          else if (azienda?.data.gruppo_aziendale?.id)
            builder.filter(
              offerta.sede.props.azienda.props.gruppo_aziendale.props.id.eq(azienda?.data.gruppo_aziendale?.id),
            )
          else
            builder.filter(
              offerta.sede.props.azienda.props.id.eq(Number(order.commessa?.sede?.azienda?.id || azienda?.data.id)),
            )
          builder.filter(
            offerta.stato.props.id
              .eq(StatoOfferta.APERTA)
              .or(order.offerta ? offerta.id.eq(Number(order.offerta?.id)) : null),
          )
          builder.expanding('sede', (sedeBuilder) => {
            sedeBuilder.expanding('azienda', (aziendaBuilder, azienda) => {
              aziendaBuilder.select('id', 'nome')
            })
            sedeBuilder.select('id', 'nome', 'azienda')
          })
          builder.expanding('analisi', (analisiBuilder, analisi) => {
            analisiBuilder.select('id', 'ded_Dis')
          })
          builder.select('id', 'ded_Dis', 'sede', 'analisi')
          builder.orderBy(offerta.ded_Dis.desc())
        })
      : undefined
  const getCommesse =
    order.commessa?.sede || clientId
      ? qsAdminApi.commessa().query((builder, commessa) => {
          if (order.commessa?.sede?.azienda?.gruppo_aziendale?.id)
            builder.filter(
              commessa.sede.props.azienda.props.gruppo_aziendale.props.id.eq(
                order.commessa.sede.azienda.gruppo_aziendale.id,
              ),
            )
          else if (azienda?.data.gruppo_aziendale?.id)
            builder.filter(
              commessa.sede.props.azienda.props.gruppo_aziendale.props.id.eq(azienda?.data.gruppo_aziendale?.id),
            )
          else
            builder.filter(
              commessa.sede.props.azienda.props.id.eq(Number(order.commessa?.sede?.azienda?.id || azienda?.data.id)),
            )
          builder.filter(
            commessa.stato.props.id
              .eq(StatoCommessa.APERTA)
              .or(order.commessa ? commessa.id.eq(Number(order.commessa?.id)) : null),
          )
          builder.expanding('sede', (sedeBuilder) => {
            sedeBuilder.expanding('azienda', (aziendaBuilder, azienda) => {
              aziendaBuilder.select('id', 'nome')
            })
            sedeBuilder.select('id', 'nome', 'azienda')
          })
          builder.select('id', 'ded_Dis', 'titolo', 'sede')
          builder.orderBy(commessa.ded_Dis.desc())
        })
      : undefined

  const getStati = qsAdminApi.stato_ordine().query((builder, stato) => {
    builder.select('id', 'nome')
    builder.orderBy(stato.nome.asc())
  })

  const getStatiAcconto = qsAdminApi.stato_acconto_ordine().query((builder, stato) => {
    builder.select('id', 'nome')
    builder.orderBy(stato.id.asc())
  })

  const getStatiFatturazione = qsAdminApi.stato_fatturazione_ordine().query((builder, stato) => {
    builder.select('id', 'nome')
    builder.orderBy(stato.id.asc())
  })

  const getAziende = qsAdminApi.azienda().query((builder, azienda) => {
    builder.filter(azienda.sedi.any())
    if (offerId)
      builder.filter(
        order.commessa?.sede?.azienda?.gruppo_aziendale
          ? azienda.gruppo_aziendale.props.id.eq(Number(order.commessa.sede.azienda.gruppo_aziendale.id))
          : order.commessa?.sede
            ? azienda.id.eq(Number(order.commessa.sede.azienda?.id))
            : null,
      )
    else builder.filter(clientId ? azienda.id.eq(Number(clientId)) : null)
    if (userInfo?.roles?.hasRole(Roles.Sales) && !userInfo.roles.hasRole(Roles.Supervisor))
      builder.filter(
        azienda.agente.props.commerciale_qs.props.id
          .eq(Number(user.data.value[0].id))
          .or(order.commessa?.sede ? azienda.id.eq(Number(order.commessa.sede.azienda?.id)) : null),
      )
    if (userInfo?.roles?.hasRole(Roles.ExternalSales))
      builder.filter(
        azienda.rivenditore.props.id
          .eq(Number(user.data.value[0].azienda?.id))
          .or(order.commessa?.sede ? azienda.id.eq(Number(order.commessa.sede.azienda?.id)) : null),
      )
    builder.orderBy(azienda.nome.asc())
    builder.select('id', 'nome')
  })

  return defer({
    title,
    order,
    getStati,
    getStatiAcconto,
    getStatiFatturazione,
    getAziende,
    getOfferte,
    getCommesse,
    userInfo,
    defaultCRUDAllowedRoles: [
      Roles.GlobalAdministrator,
      Roles.Administrator,
      Roles.Supervisor,
      Roles.Sales,
      Roles.ExternalSales,
    ],
  })
}) satisfies LoaderFunction

export const orderEditorRouteAction = (async ({ request }: RouteFunctionParams) => {
  switch (request.method) {
    case 'POST': {
      const orderFormData = await request.formData()
      console.log('orderFormData', orderFormData)
      const client = useQsAdminApiManager.getState().client
      const res = await client.post('/api/ordine/create', orderFormData)
      console.log('ORDER RES', res)
      return redirect(`./../${res.data.id}`)
    }
    case 'PUT':
    case 'PATCH': {
      const order = (await request.json()) as ordine
      const qsAdminApi = useQsAdminApiManager.getState().service
      return await qsAdminApi.ordine(order.id).update(order)
    }
    default: {
      throw new Response('Method not allowed', {
        status: 405,
        statusText: 'Method not allowed',
      })
    }
  }
}) satisfies ActionFunction

export const restoreOrderAction = (async ({ request, params }: RouteFunctionParams) => {
  const order = (await request.json()) as ordine
  const qsAdminApi = useQsAdminApiManager.getState().service
  return await qsAdminApi.ordine(order.id).RestoreOrder()
}) satisfies ActionFunction

export const assignOrderAction = (async ({ request }: RouteFunctionParams) => {
  const assignment = (await request.json()) as { orderId: number; userId: number }
  const client = useQsAdminApiManager.getState().client
  return await client.post(`/api/ordine/${assignment.orderId}/assign`, { userId: assignment.userId })
}) satisfies ActionFunction

export const removeOrderAssigneeAction = (async ({ request }: RouteFunctionParams) => {
  const order = (await request.json()) as ordine
  const qsAdminApi = useQsAdminApiManager.getState().service
  return await qsAdminApi.ordine(order.id).RemoveAssignee()
}) satisfies ActionFunction

const OrderEditorRoute = {
  path: ':orderId',
  element: <OrderEditor creating={false} isReseller={false} />,
  children: [
    {
      path: 'restore',
      action: restoreOrderAction,
      allowedRoles: [Roles.GlobalAdministrator, Roles.Administrator],
    } as RoleRouteObject,
    {
      path: 'assign',
      action: assignOrderAction,
      allowedRoles: [Roles.GlobalAdministrator, Roles.Supervisor, Roles.Organizer],
    } as RoleRouteObject,
    {
      path: 'remove-assignee',
      action: removeOrderAssigneeAction,
      allowedRoles: [Roles.GlobalAdministrator, Roles.Supervisor, Roles.Organizer],
    } as RoleRouteObject,
  ],
  loader: orderEditorRouteLoader,
  action: orderEditorRouteAction,
  allowedRoles: [Roles.Guest],
  handle: {
    crumb: (match) => ({
      label: match.data.order.ded_Dis,
    }),
  },
} as RoleRouteObject

export default OrderEditorRoute
